import type { Event } from '@sse/schema';
import type { ReactNode } from 'react';
import { STATUS_LABELS } from '../lib/format';

/** campo senza larghezza (per affiancarne più d'uno) */
export const inputBaseCls =
  'h-10 border border-fg/25 bg-page px-2 text-sm outline-none focus:border-fg disabled:bg-surface';
export const inputCls = `${inputBaseCls} w-full`;
export const btnPrimary =
  'inline-flex h-10 items-center justify-center gap-2 bg-fg px-4 text-label font-medium uppercase tracking-[0.04em] text-page hover:bg-fg-hover disabled:bg-surface-strong disabled:text-muted';
export const btnSecondary =
  'inline-flex h-10 items-center justify-center gap-2 border border-fg px-3 text-label font-medium uppercase tracking-[0.04em] hover:bg-surface-strong disabled:border-surface-strong disabled:text-muted';
export const btnGhost = 'inline-flex h-9 items-center gap-1 px-2 text-sm hover:bg-surface-strong';

const STATUS_STYLE: Record<Event['status'], string> = {
  published: 'bg-ok-soft text-fg',
  draft: 'bg-surface-strong text-fg',
  cancelled: 'bg-danger-soft text-fg',
  archived: 'bg-surface text-muted',
};

export function StatusBadge({ status }: { status: Event['status'] }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.04em] ${STATUS_STYLE[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/** Etichetta, controllo, aiuto ed errore di un campo del form. */
export function Field({
  label,
  hint,
  error,
  children,
  className = '',
}: {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1 block text-label font-medium uppercase tracking-[0.04em] text-muted">
        {label}
      </span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-muted">{hint}</span>}
      {error && (
        <span role="alert" className="mt-1 block text-xs font-medium text-danger">
          {error}
        </span>
      )}
    </label>
  );
}

/**
 * "Visibile sulla pagina" nell'intestazione di un riquadro: spento nasconde la parte senza
 * cancellarla (pagina per gli ospiti, `sections`).
 */
export function VisibleToggle({
  visible,
  onChange,
  hiddenLabel = 'Nascosta: resta salvata',
}: {
  visible: boolean;
  onChange: (v: boolean) => void;
  /** testo quando è spento (al plurale per i tasti) */
  hiddenLabel?: string;
}) {
  return (
    <label
      className={`flex items-center gap-1.5 text-xs font-medium ${visible ? '' : 'text-muted'}`}
    >
      <input type="checkbox" checked={visible} onChange={(e) => onChange(e.target.checked)} />
      {visible ? 'Visibile sulla pagina' : hiddenLabel}
    </label>
  );
}

export function Card({
  title,
  children,
  actions,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="border border-surface-strong bg-page">
      <header className="flex items-center justify-between border-b border-surface-strong px-4 py-2">
        <h2 className="text-label font-medium uppercase tracking-[0.06em]">{title}</h2>
        {actions}
      </header>
      <div className="flex flex-col gap-4 p-4">{children}</div>
    </section>
  );
}
